
import { ChevronLeft, Plus, MoreHorizontal, Share, Trash2, UserPlus, Settings } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import ButtonCircle from '@/components/ui/ButtonCircle';
import AddMemoryChatModal from '@/components/memory/chat/AddMemoryChatModal';
import ActionDrawer, { ActionButton } from '@/components/ui/action-drawer';
import { ProfileInviteDrawer } from '@/components/profile/ProfileInviteDrawer';
import { useState } from 'react';
import { TimelineSection } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useManagedProfile } from '@/hooks/useManagedProfile';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

interface TimelineNavigationProps {
  onMemoryCreated?: (sectionId: string) => void;
  onOpenSidebar?: () => void;
  isVisible?: boolean;
  className?: string;
  targetUserId?: string;
  targetUserProfile?: any;
  timelineSections?: TimelineSection[];
  sectionsLoading?: boolean;
  timelineError?: boolean;
  hasTimelineSetup?: boolean;
}

const TimelineNavigation = ({
  onMemoryCreated,
  onOpenSidebar,
  isVisible = true,
  className,
  targetUserId,
  targetUserProfile,
  timelineSections,
  sectionsLoading,
  timelineError,
  hasTimelineSetup = true
}: TimelineNavigationProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const username = params.username;
  const { t } = useTranslation(['common', 'timeline']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActionDrawerOpen, setIsActionDrawerOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const { user } = useAuth();
  
  // Check if this is a managed profile
  const { isManagedProfile, relationshipType } = useManagedProfile(targetUserId);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleShare = async () => {
    if (!hasTimelineSetup) {
      toast.error(t('setupRequired.shareMessage', { ns: 'timeline' }));
      return;
    }

    try {
      const currentUrl = window.location.href;
      const shareTitle = username ? `${username}'s Timeline auf Uplivion` : 'Meine Timeline auf Uplivion';
      const shareText = username ? `Schau dir die Timeline von ${username} an!` : 'Schau dir meine Timeline an!';

      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: currentUrl
        });
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(currentUrl);
        toast.success('Timeline-Link in die Zwischenablage kopiert!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Fehler beim Teilen');
    }
  };

  const handleAddMemory = () => {
    if (!hasTimelineSetup) {
      toast.error(t('setupRequired.addMemoryMessage', { ns: 'timeline' }));
      return;
    }

    if (onOpenSidebar) {
      onOpenSidebar();
    } else {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = (createdSectionId?: string) => {
    setIsModalOpen(false);
    if (createdSectionId && onMemoryCreated) {
      onMemoryCreated(createdSectionId);
    }
  };

  const handleResetTimeline = async () => {
    if (!targetUserId) return;
    
    setIsResetting(true);
    try {
      const { data, error } = await supabase.rpc('reset_timeline_to_setup_state', {
        target_user_id: targetUserId
      });

      if (error) throw error;

      const result = data as { success?: boolean; error?: string };
      if (result?.success) {
        toast.success(t('actions.resetSuccessTitle', { ns: 'timeline' }), {
          description: t('actions.resetSuccessDescription', { ns: 'timeline' })
        });
        // Trigger a page reload to refresh the timeline state
        window.location.reload();
      } else {
        throw new Error(result?.error || 'Failed to reset timeline');
      }
    } catch (error) {
      console.error('Failed to reset timeline:', error);
      toast.error(t('actions.resetErrorTitle', { ns: 'timeline' }), {
        description: error instanceof Error ? error.message : t('actions.resetErrorDescription', { ns: 'timeline' })
      });
    } finally {
      setIsResetting(false);
      setIsResetDialogOpen(false);
    }
  };

  // Handlers for managed profile actions
  const handleInviteToProfile = () => {
    setIsInviteDrawerOpen(true);
  };

  const handleGoToProfile = () => {
    navigate('/friends');
  };

  // Check if user can reset timeline (owns it)
  const canResetTimeline = targetUserId === user?.id || isManagedProfile;

  // Build actions array based on managed profile status
  const timelineActions: ActionButton[] = [
    {
      text: t('actions.shareTimeline', { ns: 'timeline' }),
      icon: Share,
      onClick: handleShare,
      disabled: !hasTimelineSetup,
      closeAfterAction: true
    },
    // Add managed profile specific actions
    ...(isManagedProfile ? [
      {
        text: 'Invite Friends',
        icon: UserPlus,
        onClick: handleInviteToProfile,
        disabled: !hasTimelineSetup,
        closeAfterAction: true
      },
      {
        text: 'Go to Profile Settings',
        icon: Settings,
        onClick: handleGoToProfile,
        closeAfterAction: true
      }
    ] : []),
    {
      text: t('actions.resetTimeline', { ns: 'timeline' }),
      icon: Trash2,
      variant: 'destructive' as const,
      onClick: () => setIsResetDialogOpen(true),
      disabled: !hasTimelineSetup || !canResetTimeline,
      closeAfterAction: true
    }
  ];

  return (
    <>
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className={`bottom-0 left-0 right-0 ${className || ''}`}
          >
            <div className="flex justify-between items-center py-4 px-8">
              {/* Left: Back Button */}
              <ButtonCircle onClick={handleBack} size="small" aria-label={t('buttons.back')}>
                <ChevronLeft />
              </ButtonCircle>

              {/* Center: Add Memory Button */}
              <ButtonCircle 
                onClick={handleAddMemory} 
                size="large" 
                aria-label={t('buttons.addMemory')}
                className={!hasTimelineSetup ? 'opacity-50' : ''}
              >
                <Plus />
              </ButtonCircle>

              {/* Right: Timeline Options Button */}
              <ButtonCircle 
                onClick={() => setIsActionDrawerOpen(true)} 
                size="small" 
                aria-label={t('actions.timelineOptions', { ns: 'timeline' })}
                className={!hasTimelineSetup ? 'opacity-50' : ''}
              >
                <MoreHorizontal />
              </ButtonCircle>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Memory Modal - Updated to pass timeline owner info */}
      <AddMemoryChatModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        targetUserId={targetUserId || user?.id}
        targetUserProfile={targetUserProfile}
        timelineSections={timelineSections}
        sectionsLoading={sectionsLoading}
        timelineError={timelineError}
      />

      {/* Timeline Actions Drawer */}
      <ActionDrawer
        title={isManagedProfile && relationshipType === 'gift' 
          ? `🎁 Gift Timeline Options`
          : t('actions.timelineOptions', { ns: 'timeline' })
        }
        actions={timelineActions}
        isOpen={isActionDrawerOpen}
        onClose={() => setIsActionDrawerOpen(false)}
        cancelText={t('buttons.cancel')}
      />

      {/* Profile Invite Drawer */}
      {targetUserProfile && (
        <ProfileInviteDrawer
          isOpen={isInviteDrawerOpen}
          onClose={() => setIsInviteDrawerOpen(false)}
          profile={targetUserProfile}
        />
      )}

      {/* Reset Timeline Confirmation Dialog */}
      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('actions.resetConfirmTitle', { ns: 'timeline' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('actions.resetConfirmDescription', { ns: 'timeline' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>
              {t('buttons.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetTimeline}
              disabled={isResetting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isResetting ? t('buttons.loading') : t('actions.resetConfirmButton', { ns: 'timeline' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TimelineNavigation;
